import React from 'react';
import { Calendar, Briefcase, Star, Clock, GraduationCap, Building2, MapPin, Wallet } from 'lucide-react';
import { ExpandableDescription } from './ExpandableDescription.jsx';
import { DURATION_LABELS, EXPERIENCE_LABELS } from './jobFormat.js';
import { SOURCE_LABEL } from '../../../constants/jobSources.js';

// The expanded detail panel for a single job row. Upwork rows show the client
// stats (applicants / rating / spent); feed-source rows (RemoteOK, OnlineJobs,
// Dribbble, …) have no such client history, so they show the data they DO
// carry — employer, location, pay, work type — instead of empty Upwork zeros.
export const JobDetailRow = ({ job, client, skillsList, filters }) => {
    const src = job.source || 'upwork';
    const isFeed = src !== 'upwork';

    const company = typeof client.company === 'string' ? client.company : '';
    const salaryText = typeof client.salaryText === 'string' ? client.salaryText : '';
    const jobType = typeof client.jobType === 'string' ? client.jobType : '';
    const hoursPerWeek = client.hoursPerWeek;
    // Some feed Sources (e.g. RemoteHabits) re-aggregate listings from another
    // board; `origin` names that true source so the panel shows real provenance.
    const origin = typeof client.origin === 'string' ? client.origin : '';
    const viaLabel = SOURCE_LABEL[src] || src;

    return (
    <div className="px-6 pb-6 pt-0 animate-in fade-in slide-in-from-top-1 duration-200 cursor-default">
        <div className="px-4">
            {/* Top Meta Row */}
            {isFeed ? (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-700 mb-4">
                    {company && (
                        <div className="flex items-center gap-1.5 font-bold text-gray-900">
                            <Building2 size={15} className="text-gray-400" /> {company}
                        </div>
                    )}
                    <div className="flex items-center gap-1.5 text-gray-600 font-medium">
                        <MapPin size={15} className="text-gray-400" /> {job.country || 'Remote'}
                    </div>
                    {salaryText && (
                        <div className="flex items-center gap-1.5 font-bold text-green-700">
                            <Wallet size={15} className="text-green-600" /> {salaryText}
                        </div>
                    )}
                    {jobType && (
                        <div className="flex items-center gap-1.5 text-gray-600 font-medium">
                            <Briefcase size={15} className="text-gray-400" /> {jobType}
                            {hoursPerWeek ? ` · ${hoursPerWeek} hrs/wk` : ''}
                        </div>
                    )}
                    <div className="ml-auto text-xs text-gray-400 font-medium">
                        via {origin ? `${origin} · ${viaLabel}` : viaLabel}
                    </div>
                </div>
            ) : (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-700 mb-4">
                    <div className="font-bold text-gray-900">
                        {job.total_applicants != null
                            ? `${job.total_applicants} applicant${job.total_applicants === 1 ? '' : 's'}`
                            : '— applicants'}
                        {job.freelancers_to_hire ? ` · ${job.freelancers_to_hire} to hire` : ''}
                    </div>
                    <div className="hidden sm:block text-gray-300">|</div>

                    <div className="flex items-center gap-1">
                        <div className="flex text-yellow-500">
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} size={14} className={i < Math.round(client.totalFeedback || 0) ? "fill-current" : "text-gray-300"} />
                            ))}
                        </div>
                        <span className="font-bold">{client.totalFeedback?.toFixed?.(2) || '0.00'}</span>
                        <span className="text-gray-500 text-xs">({client.totalReviews || 0} reviews)</span>
                    </div>

                    <div className="font-bold text-gray-900">
                        {(() => {
                            const spent = client.totalSpent?.rawValue || client.totalSpent;
                            return spent && !isNaN(parseFloat(spent))
                                ? `$${parseInt(spent).toLocaleString()} Spent`
                                : '$0 Spent';
                        })()}
                    </div>

                    <div className="flex items-center gap-1.5 text-gray-600 font-medium">
                        {job.country || 'Unknown Location'}
                    </div>
                </div>
            )}

            <ExpandableDescription description={job.description} filters={filters} />

            <div className="flex flex-wrap gap-2 mb-6">
                {skillsList.map((skill) => (
                    <span key={skill} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors border border-gray-200">
                        {skill}
                    </span>
                ))}
            </div>

            {/* Footer Meta Row — Upwork engagement/contract metadata (feed jobs
                carry none of these gig-specific fields, so it's Upwork-only). */}
            {!isFeed && (
                <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-xs text-gray-600 font-semibold mb-3">
                    <div className="flex items-center gap-1.5">
                        <Clock size={16} className="text-gray-400" />
                        <span>{job.engagement || job.workload || '—'}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Calendar size={16} className="text-gray-400" />
                        <span>{DURATION_LABELS[job.duration] || job.duration || '—'}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <GraduationCap size={16} className="text-gray-400" />
                        <span>{EXPERIENCE_LABELS[job.experience_level] || job.experience_level || '—'}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Briefcase size={16} className="text-gray-400" />
                        <span>{job.contract_type === 'Hourly' ? 'Ongoing project' : (job.contract_type === 'Fixed' ? 'One-time project' : '—')}</span>
                    </div>
                </div>
            )}

            <div className="text-xs text-gray-400">
                {job.category}
            </div>
        </div>
    </div>
    );
};
